import { Component, OnInit } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { StorageService } from './providers/storage/storage.service';
import { LoaderService } from './providers/loader/loader.service';
import { User, UserService } from './providers/user/user.service';
import OneSignal from 'onesignal-cordova-plugin';
import { PushService } from './providers/push/push.service';
import * as fromApp from './app.reducer';
import { select, Store } from '@ngrx/store';
import { getUser } from './providers/user/store/user.selectors';
import { AuthModalComponent } from './components/auth-modal/auth-modal.component';
import { AuthRedirectService } from './providers/auth/auth-redirect.service';
import { StatusBarService } from './providers/status-bar/status-bar.service';
import { ViewportService } from './providers/viewport/viewport.service';
import { Style } from '@capacitor/status-bar';

// Routes where the nav bar should be hidden (focused task views)
const HIDE_NAV_BAR_ROUTES = [
	'/add-horse',
	'/login',
	'/forgot-password',
];

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
	private user: User;
	private _appInit = false;
	public isDesktopViewport = false;
	public hideNavBar = false;

	constructor(
		private platform: Platform,
		private storageService: StorageService,
		private router: Router,
		private loaderService: LoaderService,
		private userService: UserService,
		private pushService: PushService,
		private store: Store<fromApp.AppState>,
		private modalController: ModalController,
		private authRedirectService: AuthRedirectService,
		private statusBarService: StatusBarService,
		private viewport: ViewportService
	) {
		// Track route changes to control nav bar visibility
		this.router.events
			.pipe(filter(event => event instanceof NavigationEnd))
			.subscribe((event: NavigationEnd) => {
				this.updateNavBarVisibility(event.urlAfterRedirects);
			});

		// Single source of truth for the desktop breakpoint (keyed to the chat
		// split-pane's media query). Stays reactive when the window is resized.
		this.viewport.isDesktop$.subscribe(isDesktop => {
			this.isDesktopViewport = isDesktop;
		});
	}

	ngOnInit() {
		this.platform.ready().then(() => this.startApp());
		this.updateNavBarVisibility(this.router.url);
	}

	private updateNavBarVisibility(url: string): void {
		// Check if current route should hide the nav bar
		const matchesStaticRoute = HIDE_NAV_BAR_ROUTES.some(route => url.startsWith(route));
		// Also hide for /:type/:pk/edit routes (e.g. /horse/abc-123/edit)
		const matchesEditRoute = /^\/(horse|tack|trailer)\/[^/]+\/edit/.test(url);
		this.hideNavBar = matchesStaticRoute || matchesEditRoute;
	}

	async openAuthModal(payload: { event?: Event; redirectUrl?: string }): Promise<void> {
		if (payload?.redirectUrl) {
			this.authRedirectService.setRedirectUrl(payload.redirectUrl);
		}
		const modal = await this.modalController.create({
			component: AuthModalComponent,
			cssClass: 'auth-modal-shell',
		});
		await modal.present();
	}

	onAddListingClick(ev: Event): void {
		if (!this.user) {
			this.openAuthModal({ event: ev, redirectUrl: '/add-horse' });
			return;
		}

		// Navigate to add-horse route - ad type selection is inline on that page
		this.router.navigate(['/add-horse']);
	}

	async startApp() {
		// Initialize StatusBar service (safely handles platforms where it's not available)
		try {
			await this.statusBarService.initialize();
			
			// Set initial StatusBar theme based on system preference
			const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
			if (prefersDark) {
				await this.statusBarService.setDarkTheme();
			} else {
				await this.statusBarService.setPrimaryTheme();
			}

			// Listen for dark mode changes
			window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', async (e) => {
				if (e.matches) {
					await this.statusBarService.setDarkTheme();
				} else {
					await this.statusBarService.setPrimaryTheme();
				}
			});
		} catch (error) {
			console.log('StatusBar not available or initialization failed:', error);
			// Not a critical error - app will work without StatusBar customization
		}

		this.store.pipe(select(getUser)).subscribe((user: User) => {
			if (!this.user && user) {
				console.log('USER SET IN APP');
				console.log(user);
			}
			this.user = user;
		});

		// Redirect the root / index.html to the first tab (home). The declarative
		// '' -> home child redirect can fail to land on some prod entry points, so
		// force it here for the bare root as well. Deep links (non-root URLs) are
		// untouched.
		const entryUrl = this.router.url;
		if (entryUrl === '/' || entryUrl === '' || entryUrl === '/index.html') {
			this.router.navigate(['/home']);
		}
		// console.log("setting OneSignal.Notifications.addEventListener('foregroundWillDisplay')");
		// OneSignal.Notifications.addEventListener('foregroundWillDisplay', this.onNotificationReceived.bind(this));

		if (!this._appInit) {
			this._appInit = true;
			console.log('_appInit: true, pushService should start...');
		}

		// const state = await OneSignal.getPermissionSubscriptionState();
		// console.log('Device state after cold start:', state);

		// // Handle notification opens
		// OneSignal.setNotificationOpenedHandler((jsonData) => {
		// 	console.log('Notification opened:', jsonData);
		// 	// Save to storage or send to backend
		// });
	}

	// async onNotificationReceived(event: any) {
	// 	console.log('onNotificationReceived() - foregroundWillDisplay - ');
	// 	console.warn('SETTING lastNotification');
	// 	await this.storageService.setItem('lastNotification', event.notification);
	// 	if (typeof event.completed !== 'undefined') {
	// 		event.complete();
	// 	}
	// }
}
